import { useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Check, Copy, Volume2, VolumeX } from "lucide-react";
import { CA, X_URL, copyText, scrollToId } from "@/data";
import { isSoundEnabled, setSoundEnabled } from "@/sound";

const LINKS = [
  { label: "Narrative", href: "#narrative" },
  { label: "Fusion", href: "#fusion" },
  { label: "Terminal", href: "#terminal" },
  { label: "Shill Lab", href: "#shill-lab" },
  { label: "Roadmap", href: "#roadmap" },
  { label: "Memes", href: "#memes" },
];

export const CoinMark = ({ className = "h-9 w-9" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path d="M12 14 L16 4 L24 10 L32 4 L36 14 Z" fill="#E5A93C" />
    <circle cx="24" cy="27" r="17" fill="#0E1015" stroke="#E5A93C" strokeWidth="2.5" />
    <circle cx="24" cy="27" r="12.5" fill="none" stroke="#B8860B" strokeWidth="1" />
    <text
      x="24"
      y="33.5"
      textAnchor="middle"
      fontFamily="Cabinet Grotesk, Impact, sans-serif"
      fontWeight="900"
      fontSize="16"
      fill="#F6C85F"
    >
      T
    </text>
  </svg>
);

export default function Navbar() {
  const [copied, setCopied] = useState(false);
  const [sound, setSound] = useState(isSoundEnabled());
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 28 });

  const handleCopy = async () => {
    await copyText(CA, "Contract address copied");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="glass fixed inset-x-0 top-0 z-50 border-b border-[rgba(229,169,60,0.18)]"
      data-testid="navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <button
          onClick={() => scrollToId("#top")}
          className="flex items-center gap-2.5"
          data-testid="nav-logo"
        >
          <CoinMark />
          <span className="font-display text-xl font-black tracking-tight text-[#F8FAFC]">
            $TATE
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollToId(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] transition-colors duration-300 hover:text-[#F6C85F]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => {
              const next = !sound;
              setSoundEnabled(next);
              setSound(next);
            }}
            data-testid="sound-toggle"
            aria-label={sound ? "Mute coin sounds" : "Enable coin sounds"}
            className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-300 ${
              sound
                ? "border-[#F6C85F] bg-[rgba(229,169,60,0.15)] text-[#F6C85F]"
                : "border-[rgba(229,169,60,0.35)] text-[#64748B] hover:text-[#F6C85F]"
            }`}
          >
            {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button
            onClick={handleCopy}
            data-testid="nav-copy-ca-button"
            className="group hidden items-center gap-2 rounded-full border border-[rgba(229,169,60,0.4)] bg-[#0E1015] px-3.5 py-2 transition-colors duration-300 hover:border-[#F6C85F] sm:flex"
          >
            <span className="font-mono2 text-[11px] text-[#94A3B8] transition-colors group-hover:text-[#F6C85F]">
              CA: {CA.slice(0, 4)}…{CA.slice(-4)}
            </span>
            {copied ? (
              <Check className="h-3.5 w-3.5 text-[#F6C85F]" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-[#94A3B8] group-hover:text-[#F6C85F]" />
            )}
          </button>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-x-button"
            className="flex items-center gap-2 rounded-full bg-[#E5A93C] px-4 py-2 font-display text-sm font-bold text-[#07080A] transition-all duration-300 hover:bg-[#F6C85F] hover:shadow-[0_0_24px_rgba(246,200,95,0.35)]"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Follow
          </a>
        </div>
      </div>
      {/* scroll progress */}
      <motion.div
        style={{ scaleX: progress }}
        className="absolute inset-x-0 bottom-0 h-[2px] origin-left bg-gradient-to-r from-[#B8860B] via-[#F6C85F] to-[#DC2626]"
        data-testid="scroll-progress"
      />
    </motion.header>
  );
}
